/**
 * useStandaloneBookings - Hook para buscar bookings sem vínculo com deal
 * Retorna dados no formato unificado SchedulingListActivity para merge na tela
 * 
 * @version 1.0.0
 * RELEASE: 2026-02-17 v1.0.0 - Implementação inicial
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { format } from 'date-fns';
import { createLogger } from '@/lib/logger';
import type { SchedulingListActivity } from '@/hooks/useSchedulingListActivities';

// Logger dedicado para este hook
const logger = createLogger('StandaloneBookings');

/** Dados brutos retornados pela RPC */
interface StandaloneBookingRow {
  id: string;
  guest_name: string;
  guest_email: string;
  guest_phone: string | null;
  guest_notes: string | null;
  start_at: string;
  end_at: string;
  status: string;
  conferencing_link: string | null;
  cancellation_reason: string | null;
  host_user_id: string;
  host_user_name: string;
  event_type_id: string;
  event_type_title: string;
  event_type_color: string | null;
  created_at: string;
  booking_timezone: string;
  cancel_token: string | null;
}

/** Parâmetros do hook */
interface UseStandaloneBookingsParams {
  startDate: Date;
  endDate: Date;
  status?: string | null;
  performedByUserId?: string | null;
  eventTypeId?: string | null;
  /** Tipo de agendamento selecionado no filtro */
  schedulingType?: string | null;
}

/**
 * Transforma booking standalone para formato unificado SchedulingListActivity
 */
function transformToActivity(row: StandaloneBookingRow): SchedulingListActivity & { is_standalone_booking: boolean; event_type_color?: string | null; cancel_token?: string | null; booking_status?: string } {
  // Monta descrição rica com dados do convidado para exibição no modal
  const descParts: string[] = [];
  descParts.push('Agendamento online');
  descParts.push(`👤 ${row.guest_name} (${row.guest_email})`);
  if (row.guest_phone) descParts.push(`📱 Telefone: ${row.guest_phone}`);
  if (row.guest_notes) descParts.push(`📝 Observações: ${row.guest_notes}`);
  const richDescription = descParts.join('<br>');

  return {
    id: row.id,
    title: `${row.guest_name} - ${row.event_type_title}`,
    description: richDescription,
    type: 'meeting',
    scheduled_at: row.start_at,
    completed_at: row.status === 'cancelled' ? row.start_at : null,
    created_at: row.created_at,
    performed_by_user_id: row.host_user_id,
    deal_id: null,
    deal_title: undefined,
    customer_name: row.guest_name,
    assigned_user_name: row.host_user_name,
    conferencing_link: row.conferencing_link,
    is_standalone_booking: true,
    event_type_color: row.event_type_color,
    cancel_token: row.cancel_token,
    booking_status: row.status === 'cancelled' ? 'cancelled' : 'confirmed',
  };
}

/**
 * Hook para buscar bookings standalone (sem deal) via RPC
 */
export function useStandaloneBookings({
  startDate,
  endDate,
  status,
  performedByUserId,
  eventTypeId,
  schedulingType,
}: UseStandaloneBookingsParams) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  // Se o filtro é 'task' ou 'meeting' (manual), não buscar standalone
  const shouldFetch = schedulingType !== 'task' && schedulingType !== 'meeting';

  const { data: bookings = [], isLoading, refetch } = useQuery({
    queryKey: [
      'standalone-bookings',
      currentTenant?.id,
      startDate.toISOString(),
      endDate.toISOString(),
      status,
      performedByUserId,
      eventTypeId,
    ],
    queryFn: async () => {
      if (!currentTenant?.id) return [];

      const startDateStr = format(startDate, 'yyyy-MM-dd');
      const endDateStr = format(endDate, 'yyyy-MM-dd');

      logger.debug('Buscando bookings standalone', { startDateStr, endDateStr });

      // Mapeia status do filtro para o status do booking
      let rpcStatus: string | null = null;
      if (status === 'completed') rpcStatus = 'cancelled'; // bookings "concluídos" = cancelados
      else if (status === 'open') rpcStatus = 'confirmed'; // bookings "abertos" = confirmados

      const { data, error } = await supabase.rpc('get_scheduling_bookings_standalone', {
        p_start_date: startDateStr,
        p_end_date: endDateStr,
        p_status: rpcStatus,
        p_user_id: performedByUserId || null,
        p_event_type_id: eventTypeId || null,
        p_limit: 100,
      } as Record<string, unknown>);

      if (error) {
        logger.error('Erro ao buscar bookings standalone:', error.message);
        throw error;
      }

      const rows = (data || []) as StandaloneBookingRow[];
      logger.debug(`Encontrados ${rows.length} bookings standalone`);
      return rows.map(transformToActivity);
    },
    enabled: !!currentTenant?.id && shouldFetch,
    staleTime: 30 * 1000,
  });

  // Mutation para vincular booking a um deal
  const linkToDeal = useMutation({
    mutationFn: async ({ bookingId, dealId }: { bookingId: string; dealId: string }) => {
      const { data, error } = await supabase.rpc('link_booking_to_deal' as any, {
        p_booking_id: bookingId,
        p_deal_id: dealId,
      });

      if (error) {
        logger.error('Erro ao vincular booking ao deal:', error.message);
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      // Invalida caches relevantes para refletir a mudança
      queryClient.invalidateQueries({ queryKey: ['standalone-bookings'] });
      queryClient.invalidateQueries({ queryKey: ['calendar-activities'] });
      queryClient.invalidateQueries({ queryKey: ['scheduling-list-activities'] });
      queryClient.invalidateQueries({ queryKey: ['scheduling-kpi'] });
    },
  });

  return {
    /** Bookings standalone transformados para formato unificado */
    bookings,
    /** Carregamento */
    isLoading: shouldFetch ? isLoading : false,
    /** Refetch manual */
    refetch,
    /** Mutation para vincular booking a deal */
    linkToDeal,
  };
}
